import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider, useT } from "./context/LanguageContext";
import { NotifProvider } from "./context/NotifContext";
import { ThemeProvider } from "./context/ThemeContext";
import Navbar from "./components/Navbar";
import ProtectedRoute from "./components/ProtectedRoute";
import SplashScreen from "./components/SplashScreen";
import { api } from "./lib/api";
import { ensurePushRegistered, onForegroundPush, pushPermission } from "./lib/push";
import usePageView from "./lib/usePageView";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Standings from "./pages/Standings";
import Leagues from "./pages/Leagues";
import LeagueDetail from "./pages/LeagueDetail";
import MyMatches from "./pages/MyMatches";
import MatchDetail from "./pages/MatchDetail";
import AdminPanel from "./pages/AdminPanel";
import H2H from "./pages/H2H";
import ChatList from "./pages/ChatList";
import DMThread from "./pages/DMThread";
import Account from "./pages/Account";
import PlayerProfile from "./pages/PlayerProfile";
import Social from "./pages/Social";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import { ByolHome, ByolCreate, ByolCaptainView, ByolInviteLanding } from "./pages/Byol";
import Classics from "./pages/Classics";
import MobileShell from "./v2/MobileShell";
import V2Gate from "./v2/V2Gate";
import MyLeagueV2 from "./v2/pages/MyLeague";
import ExploreV2 from "./v2/pages/Explore";
import SocialV2 from "./v2/pages/Social";
import ProfileV2 from "./v2/pages/Profile";
import LeagueHubV2 from "./v2/pages/LeagueHub";
import StoreV2 from "./v2/pages/Store";
import MatchDetailV2 from "./v2/pages/MatchDetailV2";
import PlayerProfileV2 from "./v2/pages/PlayerProfileV2";
import DMThreadV2 from "./v2/pages/DMThreadV2";
import H2HV2 from "./v2/pages/H2HV2";
import MyMatchesV2 from "./v2/pages/MyMatchesV2";
import AccountV2 from "./v2/pages/AccountV2";
import NotificationsV2 from "./v2/pages/NotificationsV2";
import WorldCupPreviewV2 from "./v2/pages/WorldCupPreviewV2";
import WorldCupV2 from "./v2/pages/WorldCupV2";
import WCImmersionV2 from "./v2/pages/WCImmersionV2";
// (iter 76) AdminWCImmersionV2 retired — admin lives at /admin?tab=wc26
// import AdminWCImmersionV2 from "./v2/pages/AdminWCImmersionV2";
import WCTourneyV2 from "./v2/pages/WCTourneyV2";
import DreamTeamV2 from "./v2/pages/DreamTeamV2";
// WC 48-team mode revived in iter 68 by user request (post iter 61 sunset).
import FriendliesV2 from "./v2/pages/FriendliesV2";
import ProClubsV2 from "./v2/pages/ProClubsV2";
import ProClubsFixtureV2 from "./v2/pages/ProClubsFixtureV2";
import CoopV2 from "./v2/pages/CoopV2";
import Wc2026Hub, { Wc2026Authentic, Wc2026Tournament, Wc2026Immersion, Wc2026DreamTeam, WcGateRoute } from "./pages/Wc2026Hub";
import CtfHub, { CtfAlbum, CtfMatch } from "./v2/pages/CtfHub";
import WC26LeagueV2 from "./v2/pages/WC26LeagueV2";
import UflHub from "./v2/pages/UflHub";
import UflQuickPlay from "./v2/pages/UflQuickPlay";
import UflTeams, { UflTeamDetail, UflTeamJoinByLink } from "./v2/pages/UflTeams";

function Shell() {
  const { user } = useAuth();
  const { t } = useT();
  const loc = useLocation();
  const [activeLeague, setActiveLeague] = useState(null);

  // Google OAuth callback uses URL fragment (#session_id=...) instead of a
  // query string. React Router only sees query params, so on first mount
  // (when we land on /auth/callback#session_id=...) we hoist the fragment
  // to a query string and replaceState — Router then renders cleanly.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    if (hash.startsWith("#session_id=")) {
      const sid = hash.slice("#session_id=".length);
      const target = `${window.location.pathname}?session_id=${encodeURIComponent(sid)}`;
      window.history.replaceState(null, "", target);
    }
  }, []);

  // Track page views for the admin DAU + impressions dashboard.
  usePageView();

  // Platform comes from ?platform=console|mobile in the URL; persisted so
  // deep-links keep their scope across navigation. Logged-in non-admin players'
  // backend ignores this and returns their own league.
  const urlPlatform = new URLSearchParams(loc.search).get("platform");
  const lastPlatform = typeof window !== "undefined" ? window.localStorage.getItem("eplay_platform") : null;
  const scope = (urlPlatform || lastPlatform || "").toLowerCase();
  const platformScope = scope === "console" ? "console" : scope === "mobile" ? "mobile" : null;

  useEffect(() => {
    if (urlPlatform && typeof window !== "undefined") {
      const p = urlPlatform.toLowerCase() === "console" ? "console" : "mobile";
      window.localStorage.setItem("eplay_platform", p);
    }
  }, [urlPlatform]);

  // Foreground push handler — bound once. Background handler lives in
  // /public/firebase-messaging-sw.js. If permission was previously granted,
  // silently re-sync the FCM token after login so the backend always has it.
  useEffect(() => {
    let unsub = null;
    onForegroundPush().then((u) => { unsub = u; });
    return () => { try { unsub && unsub(); } catch {} };
  }, []);

  useEffect(() => {
    if (user && user !== false && pushPermission() === "granted") {
      ensurePushRegistered({ silent: true });
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    const loadLeague = async () => {
      try {
        const qs = platformScope ? `?platform=${platformScope}` : "";
        const { data } = await api.get(`/leagues/active${qs}`);
        if (!cancelled) setActiveLeague(data);
      } catch (e) { /* noop */ }
    };
    loadLeague();
    const interval = setInterval(loadLeague, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, platformScope]);

  return (
    <div className="App min-h-screen">
      <SplashScreen />
      {!loc.pathname.startsWith("/v2") && <Navbar activeLeague={activeLeague} />}
      {loc.pathname.startsWith("/v2") ? (
        <Routes>
          <Route path="/v2" element={<V2Gate><MobileShell /></V2Gate>}>
            <Route index element={<MyLeagueV2 />} />
            <Route path="explore" element={<ExploreV2 />} />
            <Route path="social" element={<SocialV2 />} />
            <Route path="store" element={<StoreV2 />} />
            <Route path="profile" element={<ProfileV2 />} />
            <Route path="leagues/:id" element={<LeagueHubV2 />} />
            <Route path="matches/:id" element={<MatchDetailV2 />} />
            <Route path="players/:username" element={<PlayerProfileV2 />} />
            <Route path="chat/:peerId" element={<DMThreadV2 />} />
            <Route path="h2h/:opponentId" element={<H2HV2 />} />
            <Route path="my-matches" element={<MyMatchesV2 />} />
            <Route path="account" element={<AccountV2 />} />
            <Route path="notifications" element={<NotificationsV2 />} />
            <Route path="world-cup/:platform" element={<WorldCupV2 />} />
            <Route path="wc-immersion/:pool" element={<WcGateRoute><WCImmersionV2 /></WcGateRoute>} />
            <Route path="wc-immersion" element={<WcGateRoute><WCImmersionV2 /></WcGateRoute>} />
            <Route path="dream-team/:pool" element={<WcGateRoute><DreamTeamV2 /></WcGateRoute>} />
            <Route path="dream-team" element={<WcGateRoute><DreamTeamV2 /></WcGateRoute>} />
            {/* Iter 76 — legacy V2 WC Immersion admin route retired.
                All WC 2026 League admin lives at /admin?tab=wc26.
                Redirect old deep-links to the new tab. */}
            <Route path="admin/wc-immersion" element={<Navigate to="/admin?tab=wc26" replace />} />
            {/* WC 48-team test tournament sunset in iter 61.
                Redirect any stale deep-links (push notifications, old
                bookmarks) to /v2 instead of black-screening. */}
            <Route path="world-cup-tourney/:platform" element={<WcGateRoute><WCTourneyV2 /></WcGateRoute>} />
            <Route path="world-cup-tourney" element={<WcGateRoute><WCTourneyV2 /></WcGateRoute>} />
            <Route path="friendlies" element={<FriendliesV2 />} />
            <Route path="ufl/mobile" element={<UflHub />} />
            <Route path="ufl/quickplay" element={<UflQuickPlay />} />
            <Route path="ufl/teams" element={<UflTeams />} />
            <Route path="ufl/teams/:id" element={<UflTeamDetail />} />
            <Route path="ufl/join/:token" element={<UflTeamJoinByLink />} />
            <Route path="friendlies/:pool" element={<FriendliesV2 />} />
            <Route path="proclubs" element={<ProClubsV2 />} />
            <Route path="proclubs/fixtures/:id" element={<ProClubsFixtureV2 />} />
            <Route path="coop" element={<CoopV2 />} />
            <Route path="ufl/:platform" element={<WorldCupPreviewV2 />} />
            <Route path="wc2026" element={<Wc2026Hub />} />
            <Route path="wc2026/authentic" element={<Wc2026Authentic />} />
            <Route path="wc2026/tournament" element={<Wc2026Tournament />} />
            <Route path="wc2026/immersion" element={<Wc2026Immersion />} />
            <Route path="wc2026/dream-team" element={<Wc2026DreamTeam />} />
            <Route path="wc2026/ctf" element={<CtfHub />} />
            <Route path="wc2026/ctf/album" element={<CtfAlbum />} />
            <Route path="wc2026/ctf/match/:roomId" element={<CtfMatch />} />
            <Route path="wc2026/league/:pool" element={<WcGateRoute><WC26LeagueV2 /></WcGateRoute>} />
            <Route path="wc2026/league" element={<WcGateRoute><WC26LeagueV2 /></WcGateRoute>} />
          </Route>
        </Routes>
      ) : (
      <main className="max-w-7xl mx-auto px-6 py-10">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/" element={<Home />} />
          <Route path="/dashboard" element={<Dashboard activeLeague={activeLeague} />} />
          <Route path="/standings" element={<Standings activeLeague={activeLeague} />} />
          <Route path="/leagues" element={<Leagues />} />
          <Route path="/leagues/:id" element={<LeagueDetail />} />
          <Route path="/matches/:id" element={<ProtectedRoute><MatchDetail /></ProtectedRoute>} />
          <Route path="/h2h/:opponentId" element={<ProtectedRoute><H2H /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><ChatList /></ProtectedRoute>} />
          <Route path="/chat/:peerId" element={<ProtectedRoute><DMThread /></ProtectedRoute>} />
          <Route path="/my-matches" element={<ProtectedRoute><MyMatches /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/auth/callback" element={<GoogleAuthCallback />} />
          <Route path="/players/:username" element={<PlayerProfile />} />
          <Route path="/social" element={<Social />} />
          <Route path="/byol" element={<ProtectedRoute><ByolHome /></ProtectedRoute>} />
          <Route path="/byol/new" element={<ProtectedRoute><ByolCreate /></ProtectedRoute>} />
          <Route path="/byol/:id" element={<ProtectedRoute><ByolCaptainView /></ProtectedRoute>} />
          <Route path="/join/:code" element={<ByolInviteLanding />} />
          <Route path="/classics" element={<ProtectedRoute><Classics /></ProtectedRoute>} />
          <Route path="/wc2026" element={<Wc2026Hub />} />
          <Route path="/wc2026/authentic" element={<Wc2026Authentic />} />
          <Route path="/wc2026/tournament" element={<Wc2026Tournament />} />
          <Route path="/wc2026/immersion" element={<Wc2026Immersion />} />
          <Route path="/wc2026/dream-team" element={<Wc2026DreamTeam />} />
          <Route path="/wc2026/ctf" element={<CtfHub />} />
          <Route path="/wc2026/ctf/album" element={<CtfAlbum />} />
          <Route path="/wc2026/ctf/match/:roomId" element={<CtfMatch />} />
          <Route path="/admin" element={<ProtectedRoute staffOnly><AdminPanel /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      )}
      {!loc.pathname.startsWith("/v2") && (
      <footer className="border-t border-[#E4E4E7] dark:border-[#2A2A30] py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] flex justify-between flex-wrap gap-2">
          <span>{t("footer.left")}</span>
          <span>{t("footer.right")}</span>
        </div>
      </footer>
      )}
      <Toaster theme="system" position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <NotifProvider>
              <Shell />
            </NotifProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
